if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        // RAPI's quantity-break markup is loaded after the collection page has
        // initialized, so its cards do not always update Dawn's variant picker.
        // Keep the (hidden) picker as the source of truth and bridge bundle-card
        // clicks to it for the Rakhi collection quick view only.
        this.addEventListener('click', this.syncRakhiBundleVariant.bind(this), true);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            this.setupRakhiCollectionQuickView();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      isRakhiCollectionQuickView() {
        return window.location.pathname.replace(/\/$/, '') === '/collections/total-rakhi-product';
      }

      setupRakhiCollectionQuickView() {
        if (!this.isRakhiCollectionQuickView()) return;

        this.classList.add('rakhi-collection-quick-view');
        const variantPicker = this.modalContent.querySelector('variant-selects');
        if (variantPicker) {
          variantPicker.hidden = true;
          variantPicker.style.setProperty('display', 'none', 'important');
          variantPicker.setAttribute('aria-hidden', 'true');
        }
      }

      syncRakhiBundleVariant(event) {
        if (!this.isRakhiCollectionQuickView()) return;

        const clickedOption = event.target.closest('[data-bm-card]');
        if (!clickedOption || !this.modalContent.contains(clickedOption)) return;
        if (clickedOption.dataset.available !== 'true') return;

        const optionValue = clickedOption.dataset.optionValue;
        const variantId = clickedOption.dataset.variantId;
        const picker = this.modalContent.querySelector('variant-selects');
        if (!picker) return;

        const matchingInput = Array.from(picker.querySelectorAll('input[type="radio"]')).find((input) => {
          return input.value === optionValue;
        });

        const matchingSelect = Array.from(picker.querySelectorAll('select')).find((select) =>
          Array.from(select.options).some((option) => option.value === optionValue)
        );

        if (matchingInput) {
          matchingInput.checked = true;
          matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (matchingSelect) {
          const option = Array.from(matchingSelect.options).find((item) => item.value === optionValue);
          if (option) {
            matchingSelect.value = option.value;
            matchingSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        this.modalContent.querySelectorAll('[data-bm-card]').forEach((card) => {
          const isSelected = card === clickedOption;
          card.classList.toggle('bm-is-selected', isSelected);
          const radio = card.querySelector('.bm-custom-radio');
          if (radio) radio.checked = isSelected;
        });

        // Set the submitted variant immediately as well. Dawn's section-render
        // update will subsequently replace this with the same selected value.
        this.modalContent.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
          input.value = variantId;
          input.disabled = false;
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
